import axios from 'axios';

export const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper to build asset URLs (strip trailing /api if present)
export const getAssetUrl = (path) => {
  if (!path) return '';
  // If already a full URL, return as-is
  if (/^https?:\/\//i.test(path)) return path;

  // Normalize backslashes to forward slashes
  let p = String(path).replace(/\\/g, '/');

  // Find the uploads segment in common backend paths and build a relative /uploads/... path
  const uploadsIndex = p.lastIndexOf('/uploads/');
  if (uploadsIndex !== -1) {
    const rel = p.slice(uploadsIndex); // includes leading /uploads/
    const base = API_URL.replace(/\/api\/?$/, '');
    return `${base}${rel}`;
  }

  // Fallback: strip leading slashes and append to base
  const base = API_URL.replace(/\/api\/?$/, '');
  return `${base}/${p.replace(/^\/+/, '')}`;
};


// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track pending requests to prevent duplicates
const pendingRequests = new Map();

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Prevent duplicate requests
    const getRequestKey = (config) => {
      let data = config.data;
      if (data instanceof FormData) {
        // For FormData, we can't easily stringify, so we use a placeholder or specific fields
        data = '[FormData]';
      } else {
        data = JSON.stringify(data || {});
      }
      return `${config.method}-${config.url}-${data}`;
    };

    const requestKey = getRequestKey(config);

    if (pendingRequests.has(requestKey)) {
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
      console.warn('[API] Duplicate request blocked:', requestKey);
    } else {
      pendingRequests.set(requestKey, Date.now());
      config.metadata = { requestKey };
      
      // Auto-cleanup after 30s as a safety measure
      setTimeout(() => {
        if (pendingRequests.get(requestKey) && (Date.now() - pendingRequests.get(requestKey) >= 30000)) {
          pendingRequests.delete(requestKey);
        }
      }, 30001);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`[API] Response: ${response.status} ${response.config.url}`, response.data);
    // Clear pending request
    if (response.config.metadata?.requestKey) {
      pendingRequests.delete(response.config.metadata.requestKey);
    }
    return response;
  },
  (error) => {
    console.error(`[API] Error: ${error.response?.status} ${error.config?.url}`, error.response?.data || error.message);
    // Clear pending request
    if (error.config?.metadata?.requestKey) {
      pendingRequests.delete(error.config.metadata.requestKey);
    }

    // Handle auth errors — skip redirect for the login endpoint itself
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
};

// Entitlements API
export const entitlementsAPI = {
  get: () => api.get('/auth/entitlements'),
};

// Super Admin Auth API (separate token flow)
export const superAuthAPI = {
  login: (credentials) => axios.post(`${API_URL}/superauth/login`, credentials),
};

// Super Admin axios instance — uses super_token
const superApi = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' } });
superApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('super_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
superApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem('super_token');
      localStorage.removeItem('super_user');
      window.location.href = '/admin-portal-xyz789';
    }
    return Promise.reject(err);
  }
);

export const superTenantsAPI = {
  list: () => superApi.get('/tenants'),
  getById: (id) => superApi.get(`/tenants/${id}`),
  update: (id, data) => superApi.put(`/tenants/${id}`, data),
  updateOverrides: (id, data) => superApi.put(`/tenants/${id}/overrides`, data),
  usageOverview: () => superApi.get('/tenants/usage/overview'),
};

export const superSystemAPI = {
  metrics: () => superApi.get('/system-logs/metrics'),
  errors: (limit = 50) => superApi.get(`/system-logs/errors?limit=${limit}`),
  clearErrors: () => superApi.delete('/system-logs/errors'),
  featureUsage: (tenantId) => superApi.get(`/system-logs/feature-usage${tenantId ? `?tenantId=${tenantId}` : ''}`),
};

export const superTiersAPI = {
  list: () => superApi.get('/subscription-tiers'),
  registry: () => superApi.get('/subscription-tiers/registry'),
  getById: (id) => superApi.get(`/subscription-tiers/${id}`),
  create: (data) => superApi.post('/subscription-tiers', data),
  update: (id, data) => superApi.put(`/subscription-tiers/${id}`, data),
  remove: (id) => superApi.delete(`/subscription-tiers/${id}`),
  seedDefaults: () => superApi.post('/subscription-tiers/seed-defaults'),
};

export const tiersAPI = {
  hierarchy: () => api.get('/subscription-tiers/hierarchy'),
};

export const superExtensionsAPI = {
  list: () => superApi.get('/marketplace/publisher'),
  update: (id, data) => superApi.patch(`/marketplace/publisher/${id}`, data),
  updateTierPricing: (id, tierPricing) => superApi.patch(`/marketplace/publisher/${id}/tier-pricing`, { tierPricing }),
  remove: (id) => superApi.delete(`/marketplace/publisher/${id}`),
};

// Tasks API
export const tasksAPI = {
  getAll: () => api.get('/tasks'),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (taskData) => api.post('/tasks', taskData),
  update: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  delete: (id) => api.delete(`/tasks/${id}`),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  assignUser: (id, userId) => api.patch(`/tasks/${id}/assign`, { assignedTo: userId }),
  approve: (id, body) => api.post(`/tasks/${id}/approve`, body || {}),
  requestChanges: (id, body) => api.post(`/tasks/${id}/request-changes`, body || {}),
  addReview: (id, body) => api.post(`/tasks/${id}/reviews`, body || {}),
  addComment: (id, body) => api.post(`/tasks/${id}/comments`, body || {}),
  assignProject: (id, projectId) => api.patch(`/tasks/${id}/project`, { projectId }),
  getTrash: (type = 'trash') => api.get(`/tasks/deleted/trash?type=${type}`),
  restore: (id) => api.put(`/tasks/${id}/restore`),
  // Upload files for a task (FormData)
  uploadFiles: (id, formData) => api.post(`/tasks/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Delete an attachment by file path (axios allows body in delete via config.data)
  deleteAttachment: (id, filePath) => api.delete(`/tasks/${id}/attachment`, { data: { filePath } }),

  // Task history (manager/admin only)
  getHistory: (id) => api.get(`/tasks/${id}/history`),
  rollback: (id, historyId) => api.post(`/tasks/${id}/history/${historyId}/rollback`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  getProfile: () => api.get('/users/profile'),
  changePassword: (data) => api.put('/users/change-password', data),
  updateProfile: (data) => api.put('/users/update-profile', data),
  uploadProfilePicture: (formData) => api.post('/users/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteProfilePicture: () => api.delete('/users/profile-picture'),
  // Leave requests
  getLeaves: () => api.get('/users/leaves'),
  createLeave: (data) => api.post('/users/leaves', data),
  cancelLeave: (id) => api.delete(`/users/leaves/${id}`),
  getLeaveBalance: (year) => api.get('/users/leaves/balance', { params: year ? { year } : {} }),
  getPendingApprovals: () => api.get('/users/leaves/pending-approvals'),
  getAllLeaves: () => api.get('/users/leaves/all'),
  approveLeave: (id, comment) => api.put(`/users/leaves/${id}/approve`, { comment }),
  rejectLeave: (id, comment) => api.put(`/users/leaves/${id}/reject`, { comment }),
  getMyProjects: () => api.get('/users/projects'),
  getAttendance: (userId, year, month) => api.get(`/users/${userId}/attendance`, { params: { year, month } }),
};

// Leave Types API (HR/Admin manage, all users read)
export const leaveTypesAPI = {
  getAll:         ()              => api.get('/leave-types'),
  create:         (data)          => api.post('/leave-types', data),
  update:         (id, data)      => api.put(`/leave-types/${id}`, data),
  deactivate:     (id)            => api.delete(`/leave-types/${id}`),
  getBalance:     (userId, year)  => api.get(`/leave-types/balance/${userId}`, { params: year ? { year } : {} }),
  adjustBalance:  (userId, data)  => api.post(`/leave-types/balance/${userId}/adjust`, data),
};

// Admin API
export const adminAPI = {
  getOrgTree: () => api.get('/admin/org-tree'),
  getTasks: () => api.get('/admin/tasks'),
  updateUserRole: (userId, roles) => api.put(`/admin/users/${userId}/role`, { roles: Array.isArray(roles) ? roles : [roles] }),
  updateUserManager: (userId, managerId) => api.put(`/admin/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/admin/users/${userId}/department`, { department }),
  assignUserProjects: (userId, taskIds) => api.put(`/admin/users/${userId}/projects`, { taskIds }),
  // Users
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Projects
  getProjects: () => api.get('/admin/projects'),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/admin/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/admin/projects/${projectId}/members/${userId}`),
  // Password Resets
  getPasswordResets: () => api.get('/admin/password-resets'),
  resolvePasswordReset: (id, newPassword) => api.put(`/admin/password-resets/${id}`, { newPassword }),
  resetPasswordDirect: (userId, newPassword) => api.put(`/admin/users/${userId}/reset-password`, { newPassword }),
  updateUserProfile: (userId, data) => api.put(`/admin/users/${userId}/profile`, data),
  updateUserPosition: (userId, position) => api.put(`/hr/users/${userId}/position`, { position }),
  revokeUserAccess: (userId) => api.put(`/admin/users/${userId}/revoke-access`),
};

// Roles API (admin only)
export const rolesAPI = {
  listRoles: () => api.get('/admin/roles'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRole: (roleId, data) => api.put(`/admin/roles/${roleId}`, data),
  deleteRole: (roleId) => api.delete(`/admin/roles/${roleId}`),
  availablePermissions: () => api.get('/admin/roles/available-permissions'),
  getFeatures: () => api.get('/admin/features'),
  updateFeatures: (features) => api.put('/admin/features', { features }),
  getUserCustomRoles: (userId) => api.get(`/admin/users/${userId}/custom-roles`),
  setUserCustomRoles: (userId, roleIds) => api.put(`/admin/users/${userId}/custom-roles`, { roleIds }),
};

// Manager API
export const managerAPI = {
  getMyTeam: () => api.get('/manager/my-team'),
  getMyProjects: () => api.get('/manager/my-projects'),
  createProject: (data) => api.post('/manager/projects', data),
  updateProject: (id, data) => api.put(`/manager/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/manager/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/manager/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/manager/projects/${projectId}/members/${userId}`),
  // Users (Subtree)
  updateUserRole: (userId, roles) => api.put(`/manager/users/${userId}/role`, { roles: Array.isArray(roles) ? roles : [roles] }),
  updateUserManager: (userId, managerId) => api.put(`/manager/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/manager/users/${userId}/department`, { department }),
};

// HR API (mirrors admin for people management, but cannot manage admin profiles)
export const hrAPI = {
  getOrgTree: () => api.get('/hr/org-tree'),
  getTasks: () => api.get('/hr/tasks'),
  updateUserRole: (userId, roles) => api.put(`/hr/users/${userId}/role`, { roles: Array.isArray(roles) ? roles : [roles] }),
  updateUserManager: (userId, managerId) => api.put(`/hr/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/hr/users/${userId}/department`, { department }),
  updateUserPosition: (userId, position) => api.put(`/hr/users/${userId}/position`, { position }),
  // Users
  createUser: (data) => api.post('/hr/users', data),
  deleteUser: (userId) => api.delete(`/hr/users/${userId}`),
  // Projects
  getProjects: () => api.get('/hr/projects'),
  createProject: (data) => api.post('/hr/projects', data),
  updateProject: (id, data) => api.put(`/hr/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/hr/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/hr/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/hr/projects/${projectId}/members/${userId}`),
  // Password Resets
  getPasswordResets: () => api.get('/hr/password-resets'),
  resolvePasswordReset: (id, newPassword) => api.put(`/hr/password-resets/${id}`, { newPassword }),
  resetPasswordDirect: (userId, newPassword) => api.put(`/hr/users/${userId}/reset-password`, { newPassword }),
  updateUserProfile: (userId, data) => api.put(`/hr/users/${userId}/profile`, data),
  revokeUserAccess: (userId) => api.put(`/hr/users/${userId}/revoke-access`),
  // Auto-attendance config (global)
  getAutoAttendanceConfig: () => api.get('/hr/auto-attendance-config'),
  updateAutoAttendanceConfig: (data) => api.put('/hr/auto-attendance-config', data),
  // Shifts
  getShiftsHR: () => api.get('/hr/shifts'),
  createShiftHR: (data) => api.post('/hr/shifts', data),
  updateShiftHR: (id, data) => api.put(`/hr/shifts/${id}`, data),
  deleteShiftHR: (id) => api.delete(`/hr/shifts/${id}`),
  // Attendance policies
  getAttendancePolicies: (params) => api.get('/hr/attendance-policies', { params }),
  createAttendancePolicy: (data) => api.post('/hr/attendance-policies', data),
  updateAttendancePolicy: (id, data) => api.put(`/hr/attendance-policies/${id}`, data),
  deleteAttendancePolicy: (id) => api.delete(`/hr/attendance-policies/${id}`),
  applyAttendancePolicyTemplate: (data) => api.post('/hr/attendance-policies/apply-template', data),
};

// Sales API
export const salesAPI = {
  // Clients
  getClients: () => api.get('/sales/clients'),
  createClient: (data) => api.post('/sales/clients', data),
  updateClient: (id, data) => api.put(`/sales/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/sales/clients/${id}`),
  // Templates
  getTemplates: () => api.get('/sales/templates'),
  createTemplate: (formData) => api.post('/sales/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateTemplate: (id, formData) => api.put(`/sales/templates/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTemplate: (id) => api.delete(`/sales/templates/${id}`),
  // Products
  getProducts: () => api.get('/sales/products'),
  createProduct: (data) => api.post('/sales/products', data),
  updateProduct: (id, data) => api.put(`/sales/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/sales/products/${id}`),
  // Invoices
  getInvoices: () => api.get('/sales/invoices'),
  getInvoice: (id) => api.get(`/sales/invoices/${id}`),
  createInvoice: (data) => api.post('/sales/invoices', data),
  updateInvoice: (id, data) => api.put(`/sales/invoices/${id}`, data),
  updateInvoiceStatus: (id, status) => api.put(`/sales/invoices/${id}/status`, { status }),
  sendInvoice: (id, email) => api.post(`/sales/invoices/${id}/send`, email ? { email } : {}),
  convertToInvoice: (id) => api.put(`/sales/invoices/${id}/status`, { status: 'draft' }),
  deleteInvoice: (id) => api.delete(`/sales/invoices/${id}`),
};

// Messaging API
export const messagingAPI = {
  // Directory
  getDirectory: () => api.get('/messages/directory'),
  // Public keys
  uploadPublicKey: (publicKey) => api.put('/messages/keys', { publicKey }),
  getPublicKey: (userId) => api.get(`/messages/keys/${userId}`),
  // Conversations
  getConversations: () => api.get('/messages/conversations'),
  getOrCreateConversation: (participantId) => api.post('/messages/conversations', { participantId }),
  createGroup: (groupName, participantIds) => api.post('/messages/conversations/group', { groupName, participantIds }),
  editGroup: (conversationId, groupName) => api.patch(`/messages/conversations/${conversationId}`, { groupName }),
  deleteGroup: (conversationId) => api.delete(`/messages/conversations/${conversationId}`),
  addGroupMember: (conversationId, memberId) => api.post(`/messages/conversations/${conversationId}/members`, { memberId }),
  removeGroupMember: (conversationId, memberId) => api.delete(`/messages/conversations/${conversationId}/members/${memberId}`),
  togglePin: (conversationId) => api.patch(`/messages/conversations/${conversationId}/pin`),
  // Messages
  getMessages: (conversationId, params) => api.get(`/messages/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, payload) => api.post(`/messages/conversations/${conversationId}/messages`, payload),
  markRead: (conversationId) => api.patch(`/messages/conversations/${conversationId}/read`),
  markDelivered: (conversationId) => api.patch(`/messages/conversations/${conversationId}/delivered`),
  // File / audio upload
  uploadFile: (conversationId, formData) => api.post(`/messages/conversations/${conversationId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    maxBodyLength: 105 * 1024 * 1024,
    maxContentLength: 105 * 1024 * 1024,
  }),
  // Reactions
  toggleReaction: (conversationId, messageId, emoji) => api.post(`/messages/conversations/${conversationId}/messages/${messageId}/reactions`, { emoji }),
  // Threads
  getThreadReplies: (conversationId, messageId) => api.get(`/messages/conversations/${conversationId}/messages/${messageId}/thread`),
  // Link preview
  fetchLinkPreview: (url) => api.post('/messages/link-preview', { url }),
  // Tenant GIF library
  getTenantGifs: () => api.get('/messages/gifs'),
  uploadTenantGif: (formData) => api.post('/messages/gifs', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Forward message
  forwardMessage: (targetConversationId, payload) => api.post(`/messages/conversations/${targetConversationId}/forward`, payload),
};

// Email API (per-user inbox/sent/compose + IMAP/SMTP config)
export const emailAPI = {
  getConfig:    ()           => api.get('/email/config'),
  saveConfig:   (data)       => api.put('/email/config', data),
  testSmtp:     (data)       => api.post('/email/test-smtp', data),
  testImap:     (data)       => api.post('/email/test-imap', data),
  syncInbox:    ()           => api.post('/email/sync'),
  getInbox:     (params)     => api.get('/email/inbox', { params }),
  getSent:      (params)     => api.get('/email/sent',  { params }),
  getMessage:   (id)         => api.get(`/email/${id}`),
  fetchBody:    (id)         => api.post(`/email/${id}/fetch-body`),
  send:         (data)       => api.post('/email/send', data),
  markRead:     (id)         => api.patch(`/email/${id}/read`),
  toggleStar:   (id)         => api.patch(`/email/${id}/star`),
  deleteMsg:    (id)         => api.delete(`/email/${id}`),
};

// Admin Settings API
export const settingsAPI = {
  getSettings:    ()     => api.get('/settings'),
  saveSettings:   (data) => api.put('/settings', data),
  getEmailConfig: ()     => api.get('/settings/email'),
  saveEmailConfig:(data) => api.put('/settings/email', data),
  testSmtp:       (data) => api.post('/settings/email/test-smtp', data),
  testImap:       (data) => api.post('/settings/email/test-imap', data),
};

// Payroll API (HR/Admin)
export const payrollAPI = {
  // Pay Heads
  getPayHeads: () => api.get('/payroll/pay-heads'),
  createPayHead: (data) => api.post('/payroll/pay-heads', data),
  updatePayHead: (id, data) => api.put(`/payroll/pay-heads/${id}`, data),
  deletePayHead: (id) => api.delete(`/payroll/pay-heads/${id}`),
  // Salary Structures
  getSalaryStructure: (userId) => api.get(`/payroll/salary-structure/${userId}`),
  createSalaryStructure: (data) => api.post('/payroll/salary-structure', data),
  // Payroll Run & Payslips
  runPayroll: (data) => api.post('/payroll/run', data),
  getPayslips: (params) => api.get('/payroll/payslips', { params }),
  getPayslip: (id) => api.get(`/payroll/payslips/${id}`),
  finalizePayslip: (id) => api.put(`/payroll/payslips/${id}/finalize`),
  // Attendance
  getAttendance: (params) => api.get('/payroll/attendance', { params }),
  manualAttendance: (data) => api.post('/payroll/attendance/manual', data),
  // Salary Templates
  getSalaryTemplates: () => api.get('/payroll/salary-templates'),
  createSalaryTemplate: (data) => api.post('/payroll/salary-templates', data),
  updateSalaryTemplate: (id, data) => api.put(`/payroll/salary-templates/${id}`, data),
  deleteSalaryTemplate: (id) => api.delete(`/payroll/salary-templates/${id}`),
  applySalaryTemplate: (id, userId) => api.post(`/payroll/salary-templates/${id}/apply`, { userId }),
  // Shifts
  getShifts: () => api.get('/payroll/shifts'),
  createShift: (data) => api.post('/payroll/shifts', data),
  updateShift: (id, data) => api.put(`/payroll/shifts/${id}`, data),
  deleteShift: (id) => api.delete(`/payroll/shifts/${id}`),
  // Working Hours
  getWorkingHours: () => api.get('/payroll/working-hours'),
  upsertWorkingHours: (data) => api.post('/payroll/working-hours', data),
  deleteWorkingHours: (id) => api.delete(`/payroll/working-hours/${id}`),
};

// Accounts API
export const accountsAPI = {
  getCheckpoints:     (invoiceId)                        => api.get(`/accounts/invoices/${invoiceId}/checkpoints`),
  createCheckpoints:  (invoiceId, checkpoints)           => api.post(`/accounts/invoices/${invoiceId}/checkpoints`, { checkpoints }),
  markCheckpointPaid: (invoiceId, checkpointId, notes)   => api.patch(`/accounts/invoices/${invoiceId}/checkpoints/${checkpointId}`, { notes }),
  markInvoicePaid:    (invoiceId, paymentNotes)          => api.patch(`/accounts/invoices/${invoiceId}/mark-paid`, { paymentNotes }),
};

// Reimbursement API
export const reimbursementAPI = {
  submit: (formData) => api.post('/reimbursements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMy: () => api.get('/reimbursements/my'),
  getAll: (status) => api.get('/reimbursements', { params: status ? { status } : {} }),
  approve:  (id)         => api.put(`/reimbursements/${id}/approve`),
  reject:   (id, reason) => api.put(`/reimbursements/${id}/reject`, { reason }),
  markPaid: (id)         => api.put(`/reimbursements/${id}/paid`),
};

// Announcements API
export const announcementsAPI = {
  getActive: () => api.get('/announcements/active'),
  getAll: () => api.get('/announcements'),
  create: (formData) => api.post('/announcements', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => api.delete(`/announcements/${id}`),
};

// Invites API
export const invitesAPI = {
  create: (data) => api.post('/invites', data),
  getAll: () => api.get('/invites'),
  remove: (id) => api.delete(`/invites/${id}`),
  validate: (code) => axios.get(`${API_URL}/invites/validate/${code}`), // no auth header needed
};

// CRM API
export const crmAPI = {
  getDashboard:      ()                    => api.get('/crm/dashboard'),
  // Projects
  getProjects:       ()                    => api.get('/crm/projects'),
  createProject:     (data)               => api.post('/crm/projects', data),
  updateProject:     (id, data)           => api.put(`/crm/projects/${id}`, data),
  patchProject:      (id, data)           => api.patch(`/crm/projects/${id}`, data),
  deleteProject:     (id)                 => api.delete(`/crm/projects/${id}`),
  // Clients
  getClients:        ()                   => api.get('/crm/clients'),
  createClient:      (data)               => api.post('/crm/clients', data),
  updateClient:      (id, data)           => api.put(`/crm/clients/${id}`, data),
  deleteClient:      (id)                 => api.delete(`/crm/clients/${id}`),
  // Quotes
  cloneQuote:        (id, data)           => api.post(`/crm/quotes/${id}/clone`, data),
  getQuotes:         ()                   => api.get('/crm/quotes'),
  getQuoteFamily:    (rootId)             => api.get(`/crm/quotes/family/${rootId}`),
  // Scope log
  getScopeLog:       (projectId)          => api.get(`/crm/projects/${projectId}/scope-log`),
  addScopeChange:    (projectId, data)    => api.post(`/crm/projects/${projectId}/scope-log`, data),
  updateScopeChange: (pId, cId, data)     => api.put(`/crm/projects/${pId}/scope-log/${cId}`, data),
  deleteScopeChange: (pId, cId)           => api.delete(`/crm/projects/${pId}/scope-log/${cId}`),
  // Documents
  getDocuments:      (projectId)          => api.get(`/crm/projects/${projectId}/documents`),
  uploadDocument:    (projectId, fd)      => api.post(`/crm/projects/${projectId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument:    (projectId, docId)   => api.delete(`/crm/projects/${projectId}/documents/${docId}`),
  // Client overview
  getClientOverview: (clientId)           => api.get(`/crm/clients/${clientId}/overview`),
};

export const poolTasksAPI = {
  list:    (params = {}) => api.get('/pool-tasks', { params }),
  create:  (data)        => api.post('/pool-tasks', data),
  update:  (id, data)    => api.patch(`/pool-tasks/${id}`, data),
  remove:  (id)          => api.delete(`/pool-tasks/${id}`),
  claim:   (id)          => api.post(`/pool-tasks/${id}/claim`),
  approve: (id)          => api.post(`/pool-tasks/${id}/approve`),
  reject:  (id)          => api.post(`/pool-tasks/${id}/reject`),
  release: (id)          => api.post(`/pool-tasks/${id}/release`),
  complete:(id)          => api.post(`/pool-tasks/${id}/complete`),
  uploadAttachments: (id, formData) => api.post(`/pool-tasks/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeAttachment:  (id, filename) => api.delete(`/pool-tasks/${id}/attachments/${filename}`),
};

export const voiceChannelAPI = {
  list:   ()     => api.get('/voice-channels'),
  create: (data) => api.post('/voice-channels', data),
  remove: (id)   => api.delete(`/voice-channels/${id}`),
  join:   (id)   => api.post(`/voice-channels/${id}/join`),
  leave:  (id)   => api.post(`/voice-channels/${id}/leave`),
};

export const holidaysAPI = {
  getAll:  (year) => api.get('/holidays', { params: year ? { year } : {} }),
  create:  (data) => api.post('/holidays', data),
  update:  (id, data) => api.put(`/holidays/${id}`, data),
  remove:  (id)   => api.delete(`/holidays/${id}`),
};

export const inventoryAPI = {
  getAll:   (params = {}) => api.get('/inventory', { params }),
  create:   (data)        => api.post('/inventory', data),
  update:   (id, data)    => api.put(`/inventory/${id}`, data),
  remove:   (id)          => api.delete(`/inventory/${id}`),
  assign:   (id, userId)  => api.put(`/inventory/${id}/assign`, { userId }),
  unassign: (id)          => api.put(`/inventory/${id}/unassign`),
};

export const departmentAPI = {
  list:         ()             => api.get('/departments'),
  create:       (data)         => api.post('/departments', data),
  update:       (id, data)     => api.put(`/departments/${id}`, data),
  remove:       (id)           => api.delete(`/departments/${id}`),
  addMember:    (id, userId)   => api.post(`/departments/${id}/members`, { userId }),
  removeMember: (id, userId)   => api.delete(`/departments/${id}/members/${userId}`),
};

export const supportTicketAPI = {
  list:        (view = 'mine')     => api.get(`/support-tickets?view=${view}`),
  create:      (data)              => api.post('/support-tickets', data),
  get:         (id)                => api.get(`/support-tickets/${id}`),
  reply:       (id, message)       => api.post(`/support-tickets/${id}/replies`, { message }),
  setStatus:   (id, status)        => api.put(`/support-tickets/${id}/status`, { status }),
};

// Billing & Subscription API (admin only)
export const billingAPI = {
  summary:            ()                   => api.get('/billing/summary'),
  // Full tier list with features/limits for the comparison table (admin only)
  tiers:              ()                   => api.get('/billing/tiers'),
  // Public hierarchy — name/slug/price only; any authenticated user
  tiersHierarchy:     ()                   => api.get('/subscription-tiers/hierarchy'),
  upgrade:            (tierId, quantity)   => api.post('/billing/upgrade', { tierId, quantity }),
  addonStorage:       (gb)                 => api.post('/billing/addon-storage', { gb }),
  confirmAddon:       (data)               => api.post('/billing/addon-storage/confirm', data),
  syncQuantity:       ()                   => api.post('/billing/sync-quantity'),
  export:             ()                   => api.get('/billing/export', { responseType: 'blob' }),
};

// ── LMS — Leads ───────────────────────────────────────────────────────────────
export const lmsAPI = {
  // Leads
  getLeads:        (params = {})         => api.get('/crm/leads', { params }),
  getLead:         (id)                  => api.get(`/crm/leads/${id}`),
  createLead:      (data)                => api.post('/crm/leads', data),
  updateLead:      (id, data)            => api.patch(`/crm/leads/${id}`, data),
  deleteLead:      (id)                  => api.delete(`/crm/leads/${id}`),
  moveStage:       (id, status)          => api.post(`/crm/leads/${id}/stage`, { status }),
  bulkReassign:    (data)                => api.post('/crm/leads/bulk/reassign', data),
  bulkTag:         (data)                => api.post('/crm/leads/bulk/tag', data),

  // Contacts
  addContact:      (id, data)            => api.post(`/crm/leads/${id}/contacts`, data),
  updateContact:   (id, cid, data)       => api.patch(`/crm/leads/${id}/contacts/${cid}`, data),
  deleteContact:   (id, cid)             => api.delete(`/crm/leads/${id}/contacts/${cid}`),

  // Activities
  getActivities:   (leadId)              => api.get(`/crm/activities/lead/${leadId}`),
  logActivity:     (leadId, data)        => api.post(`/crm/activities/lead/${leadId}`, data),
  editActivity:    (id, data)            => api.patch(`/crm/activities/${id}`, data),
  addAddendum:     (id, text)            => api.post(`/crm/activities/${id}/addendum`, { text }),

  // Follow-ups
  getFollowUps:    (leadId)              => api.get(`/crm/followups/lead/${leadId}`),
  getMyFollowUps:  (params = {})         => api.get('/crm/followups/mine', { params }),
  createFollowUp:  (leadId, data)        => api.post(`/crm/followups/lead/${leadId}`, data),
  updateFollowUp:  (id, data)            => api.patch(`/crm/followups/${id}`, data),
  snoozeFollowUp:  (id, data)            => api.post(`/crm/followups/${id}/snooze`, data),
  completeFollowUp:(id)                  => api.post(`/crm/followups/${id}/complete`, {}),
  deleteFollowUp:  (id)                  => api.delete(`/crm/followups/${id}`),

  // Templates
  getTemplates:    (params = {})         => api.get('/crm/templates', { params }),
  getTemplate:     (id)                  => api.get(`/crm/templates/${id}`),
  createTemplate:  (data)                => api.post('/crm/templates', data),
  updateTemplate:  (id, data)            => api.patch(`/crm/templates/${id}`, data),
  newTemplateVer:  (id, data)            => api.post(`/crm/templates/${id}/versions`, data),
  deleteTemplate:  (id)                  => api.delete(`/crm/templates/${id}`),

  // Analytics
  getSummary:      ()                    => api.get('/crm/analytics/summary'),
  getFunnel:       (params = {})         => api.get('/crm/analytics/funnel', { params }),
  getLeaderboard:  (params = {})         => api.get('/crm/analytics/leaderboard', { params }),
  getStale:        ()                    => api.get('/crm/analytics/stale'),
  getForecast:     ()                    => api.get('/crm/analytics/forecast'),
  getHeatmap:      (params = {})         => api.get('/crm/analytics/activity-heatmap', { params }),

  // Pipelines
  getPipelines:    ()                    => api.get('/crm/pipelines'),
  createPipeline:  (data)                => api.post('/crm/pipelines', data),
  updatePipeline:  (id, data)            => api.patch(`/crm/pipelines/${id}`, data),
  reorderStages:   (id, order)           => api.patch(`/crm/pipelines/${id}/stages/reorder`, { order }),
  deletePipeline:  (id)                  => api.delete(`/crm/pipelines/${id}`),

  // Audit log
  getLeadAudit:    (leadId, params = {}) => api.get(`/crm/audit/lead/${leadId}`, { params }),
  getAudit:        (params = {})         => api.get('/crm/audit', { params }),

  // Custom fields
  getCustomFields: ()                    => api.get('/crm/custom-fields'),
  saveCustomFields:(fields)              => api.put('/crm/custom-fields', { fields }),

  // WhatsApp
  sendWhatsApp:    (leadId, data)        => api.post(`/crm/leads/${leadId}/whatsapp`, data),

  // CSV import
  importLeads:     (fd)                  => api.post('/crm/leads/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Score history (F3)
  getScoreHistory: (leadId)              => api.get(`/crm/leads/${leadId}/score-history`),

  // Reassignment history (F9)
  getReassignments:(leadId)              => api.get(`/crm/leads/${leadId}/reassignments`),

  // Duplicate detection + merge (F8)
  getDuplicates:   (leadId)              => api.get(`/crm/leads/${leadId}/duplicates`),
  mergeLeads:      (data)                => api.post('/crm/leads/merge', data),

  // Bulk enroll in sequence (F7)
  bulkEnrollSequence:(data)              => api.post('/crm/leads/bulk/enroll-sequence', data),

  // Regions (F1)
  getRegions:      (params = {})         => api.get('/crm/regions', { params }),
  createRegion:    (data)                => api.post('/crm/regions', data),
  updateRegion:    (id, data)            => api.patch(`/crm/regions/${id}`, data),
  deleteRegion:    (id)                  => api.delete(`/crm/regions/${id}`),

  // Routing rules (F2)
  getRoutingRules:    ()                 => api.get('/crm/routing-rules'),
  createRoutingRule:  (data)             => api.post('/crm/routing-rules', data),
  updateRoutingRule:  (id, data)         => api.patch(`/crm/routing-rules/${id}`, data),
  deleteRoutingRule:  (id)               => api.delete(`/crm/routing-rules/${id}`),
  reorderRoutingRules:(data)             => api.post('/crm/routing-rules/reorder', data),

  // Quotas (F6)
  getQuotas:       (params = {})         => api.get('/crm/quotas', { params }),
  getQuotasProgress:(params = {})         => api.get('/crm/quotas/progress', { params }),
  createQuota:     (data)                => api.post('/crm/quotas', data),
  deleteQuota:     (id)                  => api.delete(`/crm/quotas/${id}`),

  // Sequences (F7)
  getSequences:        (params = {})         => api.get('/crm/sequences', { params }),
  createSequence:      (data)                => api.post('/crm/sequences', data),
  updateSequence:      (id, data)            => api.patch(`/crm/sequences/${id}`, data),
  deleteSequence:      (id)                  => api.delete(`/crm/sequences/${id}`),
  enrollInSequence:    (sequenceId, leadId)  => api.post(`/crm/sequences/${sequenceId}/enroll`, { leadId }),
  getSequenceEnrollments:(sequenceId)        => api.get(`/crm/sequences/${sequenceId}/enrollments`),
  getLeadEnrollments:  (leadId)              => api.get(`/crm/leads/${leadId}/enrollments`),
  pauseEnrollment:     (enrollmentId)        => api.post(`/crm/sequences/enrollments/${enrollmentId}/pause`),
  resumeEnrollment:    (enrollmentId)        => api.post(`/crm/sequences/enrollments/${enrollmentId}/resume`),
  cancelEnrollment:    (enrollmentId)        => api.post(`/crm/sequences/enrollments/${enrollmentId}/cancel`),

  // Analytics — new endpoints (F4, F5, F10)
  getVelocity:         (params = {})         => api.get('/crm/analytics/velocity', { params }),
  getStuck:            (params = {})         => api.get('/crm/analytics/stuck', { params }),
  getCompetitive:      (params = {})         => api.get('/crm/analytics/competitive', { params }),
  getProducts:         (params = {})         => api.get('/crm/analytics/products', { params }),
  getSourceAttribution:(params = {})         => api.get('/crm/analytics/source-attribution', { params }),
  getTimeToWin:        (params = {})         => api.get('/crm/analytics/time-to-win', { params }),
  getCohort:           (params = {})         => api.get('/crm/analytics/cohort', { params }),
  getLeaderboardQuota: (params = {})         => api.get('/crm/analytics/leaderboard-quota', { params }),

  // Goals
  getGoals:            (userId)              => api.get(`/crm/goals/${userId}`),
  createGoal:          (userId, data)        => api.post(`/crm/goals/${userId}`, data),
  updateGoal:          (userId, gid, data)   => api.patch(`/crm/goals/${userId}/${gid}`, data),
  deleteGoal:          (userId, gid)         => api.delete(`/crm/goals/${userId}/${gid}`),

  // LMS Services
  getLMSServices:      ()                    => api.get('/crm/lms-services'),
  createLMSService:    (data)                => api.post('/crm/lms-services', data),
  updateLMSService:    (id, data)            => api.patch(`/crm/lms-services/${id}`, data),
  deleteLMSService:    (id)                  => api.delete(`/crm/lms-services/${id}`),

  // Motivational Quotes
  getRandomQuote:      ()                    => api.get('/crm/lms-quotes/random'),
  getLMSQuotes:        (params = {})         => api.get('/crm/lms-quotes', { params }),
  createLMSQuote:      (data)                => api.post('/crm/lms-quotes', data),
  deleteLMSQuote:      (id)                  => api.delete(`/crm/lms-quotes/${id}`),

  // LMS Performance dashboard + stats
  getLMSDashboard:     ()                    => api.get('/crm/lms-performance/dashboard'),
  getLMSStats:         (period = '30')       => api.get('/crm/lms-performance/stats', { params: { period } }),
  getWorkload:         ()                    => api.get('/crm/lms-performance/workload'),

  // Distribute leads (fewest-first)
  distribute:          (data)               => api.post('/crm/leads/distribute', data),

  // Import (preview + execute)
  importLeadsPreview:  (fd)                 => api.post('/crm/leads/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importLeadsExecute:  (data)               => api.post('/crm/leads/import/execute', data),

  // BD users list (for goals / distribute user picker)
  getBDUsers:          (params = {})        => api.get('/users/directory', { params }),

  // Lead Categories
  getLeadCategories:   ()                   => api.get('/crm/lead-categories'),
  createLeadCategory:  (data)               => api.post('/crm/lead-categories', data),
  updateLeadCategory:  (id, data)           => api.patch(`/crm/lead-categories/${id}`, data),
  deleteLeadCategory:  (id)                 => api.delete(`/crm/lead-categories/${id}`),

  // Lead Sources
  getLeadSources:      ()                   => api.get('/crm/lead-sources'),
  createLeadSource:    (data)               => api.post('/crm/lead-sources', data),
  updateLeadSource:    (id, data)           => api.patch(`/crm/lead-sources/${id}`, data),
  deleteLeadSource:    (id)                 => api.delete(`/crm/lead-sources/${id}`),
};

// ── LMS — Webhooks ────────────────────────────────────────────────────────────
export const webhooksAPI = {
  getEvents:       ()        => api.get('/webhooks/events'),
  getAll:          ()        => api.get('/webhooks'),
  create:          (data)    => api.post('/webhooks', data),
  update:          (id, data)=> api.patch(`/webhooks/${id}`, data),
  regenSecret:     (id)      => api.post(`/webhooks/${id}/regenerate-secret`),
  test:            (id)      => api.post(`/webhooks/${id}/test`),
  remove:          (id)      => api.delete(`/webhooks/${id}`),
};

// ── Marketplace ───────────────────────────────────────────────────────────────
export const marketplaceAPI = {
  // Tenant
  list:       (params = {}) => api.get('/marketplace', { params }),
  installed:  ()            => api.get('/marketplace/installed'),
  install:    (id)          => api.post(`/marketplace/${id}/install`),
  uninstall:  (id)          => api.delete(`/marketplace/${id}/uninstall`),
  getConfig:  (id)          => api.get(`/marketplace/${id}/config`),
  saveConfig: (id, config)  => api.patch(`/marketplace/${id}/config`, { config }),
  // Publisher (super-admin)
  publisherList:   ()           => superApi.get('/marketplace/publisher'),
  publisherCreate: (data)       => superApi.post('/marketplace/publisher', data),
  publisherUpdate: (id, data)   => superApi.patch(`/marketplace/publisher/${id}`, data),
  publisherDelete: (id)         => superApi.delete(`/marketplace/publisher/${id}`),
};

export const rawMaterialsAPI = {
  list:           (params = {}) => api.get('/raw-materials', { params }),
  get:            (id) => api.get(`/raw-materials/${id}`),
  create:         (data) => api.post('/raw-materials', data),
  update:         (id, data) => api.patch(`/raw-materials/${id}`, data),
  delete:         (id) => api.delete(`/raw-materials/${id}`),
  stockIn:        (id, data) => api.post(`/raw-materials/${id}/stock/in`, data),
  stockOut:       (id, data) => api.post(`/raw-materials/${id}/stock/out`, data),
  stockAdjust:    (id, data) => api.post(`/raw-materials/${id}/stock/adjust`, data),
  stockEntries:   (id, params = {}) => api.get(`/raw-materials/${id}/stock`, { params }),
  listPOs:        (params = {}) => api.get('/raw-materials/purchase-orders', { params }),
  createPO:       (data) => api.post('/raw-materials/purchase-orders', data),
  updatePO:       (id, data) => api.patch(`/raw-materials/purchase-orders/${id}`, data),
  receivePO:      (id) => api.post(`/raw-materials/purchase-orders/${id}/receive`),
};

export const bugReportsAPI = {
  list:           (params = {}) => api.get('/bug-reports', { params }),
  get:            (id) => api.get(`/bug-reports/${id}`),
  create:         (data) => api.post('/bug-reports', data),
  update:         (id, data) => api.patch(`/bug-reports/${id}`, data),
  delete:         (id) => api.delete(`/bug-reports/${id}`),
  addComment:     (id, text) => api.post(`/bug-reports/${id}/comments`, { text }),
  deleteComment:  (id, commentId) => api.delete(`/bug-reports/${id}/comments/${commentId}`),
  stats:          (projectId) => api.get(`/bug-reports/stats/${projectId}`),
};

export const locationAPI = {
  log:            (data) => api.post('/location/log', data),
  latest:         () => api.get('/location/latest'),
  trail:          (userId, params = {}) => api.get(`/location/trail/${userId}`, { params }),
  active:         () => api.get('/location/active'),
};

export const aiNotesAPI = {
  getConfig:    ()         => api.get('/ai-notes/config'),
  saveConfig:   (data)     => api.put('/ai-notes/config', data),
  process:      (data)     => api.post('/ai-notes/process', data),
  getUsage:     ()         => api.get('/ai-notes/usage'),
  list:         ()         => api.get('/ai-notes'),
  create:       (data)     => api.post('/ai-notes', data),
  update:       (id, data) => api.put(`/ai-notes/${id}`, data),
  remove:       (id)       => api.delete(`/ai-notes/${id}`),
};

export const faceClockInAPI = {
  status:           () => api.get('/face-clockin/status'),
  statusForUser:    (userId) => api.get('/face-clockin/status', { params: { userId } }),
  enroll:           (descriptor, targetUserId) => api.post('/face-clockin/enroll', { descriptor, targetUserId }),
  unenroll:         () => api.delete('/face-clockin/enroll'),
  unenrollUser:     (targetUserId) => api.delete('/face-clockin/enroll', { params: { targetUserId } }),
  nonce:            () => api.get('/face-clockin/nonce'),
  verify:           (descriptor, nonce) => api.post('/face-clockin/verify', { descriptor, nonce }),
  teamStatus:       () => api.get('/face-clockin/team-status'),
};

export const dialerAPI = {
  callerLookup:    (phone)               => api.get('/dialer/caller-lookup', { params: { phone } }),
  startCall:       (data)                => api.post('/dialer/call/start', data),
  endCall:         (data)                => api.post('/dialer/call/end', data),
  logPersonal:     (data)                => api.post('/dialer/call/log-personal', data),
  linkLead:        (data)                => api.post('/dialer/call/link-lead', data),
  getCallHistory:  (leadId, params = {}) => api.get(`/dialer/calls/${leadId}`, { params }),
  // <audio> can't set Authorization headers; verifyToken checks req.query.token as fallback
  getRecordingUrl: (callLogId)           => `${API_URL}/dialer/calls/recording/${callLogId}?token=${localStorage.getItem('token')}`,
};

export default api;
